// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what an HTML page shown in Konvo's document viewer may load from the web. Pages people
// send each other often build themselves from a CDN (Tailwind's Play CDN, Leaflet, Chart.js) and
// draw maps from tile servers. The viewer's policy blocks the web, so Konvo fetches scripts and
// stylesheets from these few CDNs itself and puts them in the page, and shows pictures from these
// hosts through its media proxy. Everything else stays blocked.

import {z} from 'zod';

/** CDNs whose scripts and stylesheets a page may use. */
export const HTML_LIBRARY_HOSTS: ReadonlyArray<string> = ['cdn.tailwindcss.com', 'unpkg.com', 'cdn.jsdelivr.net'];

/**
 * Hosts a page's pictures may come from, through the media proxy: the CDNs (a library's own
 * pictures, such as Leaflet's markers) and the common map tile servers. `*.` covers subdomains.
 */
export const HTML_PICTURE_HOSTS: ReadonlyArray<string> = [
	...HTML_LIBRARY_HOSTS,
	'tile.openstreetmap.org',
	'*.tile.openstreetmap.org',
	'*.tile.openstreetmap.fr',
	'tile.opentopomap.org',
	'*.tile.opentopomap.org',
	'server.arcgisonline.com',
	'services.arcgisonline.com',
	'*.basemaps.cartocdn.com',
	'tiles.stadiamaps.com',
];

/** Longest library or picture address Konvo fetches or signs. */
export const HTML_WEB_URL_MAX = 2048;
/** Pictures signed in one request. */
export const HTML_WEB_PICTURES_MAX = 40;

const hostMatches = (host: string, hosts: ReadonlyArray<string>) =>
	hosts.some((allowed) => (allowed.startsWith('*.') ? host.endsWith(allowed.slice(1)) : host === allowed));

/** An https address on one of `hosts` (protocol-relative `//host/…` counts as https), else null. */
function allowedUrl(raw: string, hosts: ReadonlyArray<string>, schemes: ReadonlyArray<string>): string | null {
	const value = raw.trim().replace(/^\/\//, 'https://');
	if (value.length > HTML_WEB_URL_MAX) return null;
	try {
		const url = new URL(value);
		if (!schemes.includes(url.protocol) || url.username || url.password || url.port) return null;
		return hostMatches(url.hostname.toLowerCase(), hosts) ? url.href : null;
	} catch {
		return null;
	}
}

/** A script or stylesheet address on one of the CDNs, as a full https address; else null. */
export function htmlLibraryUrl(raw: string): string | null {
	return allowedUrl(raw, HTML_LIBRARY_HOSTS, ['https:']);
}

/** A picture address on one of the picture hosts (http or https); else null. */
export function htmlPictureUrl(raw: string): string | null {
	return allowedUrl(raw, HTML_PICTURE_HOSTS, ['https:', 'http:']);
}

/** One script or stylesheet from a CDN, as fetched. `url` is where it ended up after redirects. */
export const HtmlLibrarySchema = z.object({
	url: z.string(),
	type: z.enum(['script', 'style']),
	text: z.string(),
});
export type HtmlLibrary = z.infer<typeof HtmlLibrarySchema>;

/** Pictures a page loads from the picture hosts, to be signed as media proxy addresses. */
export const HtmlWebPicturesRequestSchema = z
	.object({
		urls: z.array(z.string().max(HTML_WEB_URL_MAX)).min(1).max(HTML_WEB_PICTURES_MAX),
	})
	.strict();
export type HtmlWebPicturesRequest = z.infer<typeof HtmlWebPicturesRequestSchema>;
/** Each picture, as its media proxy address; ones not on a picture host are left out. */
export const HtmlWebPicturesSchema = z.object({images: z.record(z.string(), z.string())});
export type HtmlWebPictures = z.infer<typeof HtmlWebPicturesSchema>;
